import slugify from "slugify";
import type { ISbStoriesParams } from "storyblok-js-client";
import StoryblokClient from "storyblok-js-client";
import { type CMSMultiStoryResult, type CMSStory } from "../../adapters";
import { type ExceptionsStatusCodes, Exceptions } from "../../exceptions";
import { type AppLocale } from "../../locales";

export enum ApiMethod {
  GET = "GET",
  POST = "POST",
  PATCH = "PATCH",
  DELETE = "DELETE",
}

type OptionalReturn<T> = T extends undefined ? void : T;

export type CmsVersion = "published" | "draft";

const CMS_BASE_SLUG = "cdn/stories";
const LOCAL_CMS_URL = "http://localhost:3000/api";

const defaultResponse = new Response(null, {
  status: 500,
});

type FetchApiParams = {
  method: ApiMethod;
  url: string;
  headers?: Record<string, string>;
  data?: unknown;
  getAccessToken?: (() => Promise<string | undefined>) | undefined;
  locale?: AppLocale;
  origin?: string;
  signal?: AbortSignal;
};

let getInternalAccessToken: (() => Promise<string | undefined>) | undefined = undefined;

const fetchApi = async <T = undefined>({
  method,
  url,
  headers = {},
  data,
  getAccessToken,
  locale,
  origin,
  signal,
}: FetchApiParams): Promise<OptionalReturn<T>> => {
  const defaultHeaders: Record<string, string> = {
    "Content-Type": "application/json",
    Origin: origin ?? "",
  };

  // Access token must be obtained during the fetch call because obtaining it during
  // API creation leads to an inifite loop of React re-renders due to a bug in Auth0
  // https://community.auth0.com/t/using-auth0-auth0-react-and-getaccesstokensilently-in-useeffect-leads-to-infinite-loop/104855

  const accessToken = await getAccessToken?.();
  const internalAccessToken = await getInternalAccessToken?.();

  if (accessToken) defaultHeaders["Authorization"] = `Bearer ${accessToken}`;
  else if (internalAccessToken) defaultHeaders["Authorization"] = `Bearer ${internalAccessToken}`;

  if (locale) defaultHeaders["Content-Language"] = locale;

  try {
    const response = await fetch(url, {
      method: method,
      headers: {
        ...defaultHeaders,
        ...headers,
      },
      body: data ? JSON.stringify(data) : null,
      signal,
    });

    const responseText = await response.text();

    // if !response.ok treat that as 500 exception, otherwise use the status code

    const error = Exceptions[response.status as ExceptionsStatusCodes];
    if (error) {
      const errResponse = new error({
        message: `Error ${response.status} for ${url}`,
        response: {
          ...response,
          status: response.status,
          statusText: response.statusText,
          ok: response.ok,
          redirected: response.redirected,
          type: response.type,
          url: response.url,
          bodyUsed: response.bodyUsed,
        },
        stack: `httpService.fetchApi:${url}, ${responseText}, ${data ? JSON.stringify(data) : ""}`,
      });
      throw errResponse;
    }

    if (responseText) return JSON.parse(responseText) as OptionalReturn<T>;
    return undefined as OptionalReturn<T>;
  } catch (e) {
    if (e instanceof Exceptions.Exception) throw e;
    else {
      throw new Exceptions[500]({
        message: `Internal Server Error for ${url}`,
        response: defaultResponse,
        stack: `httpService.fetchApi:${url}, ${
          e instanceof Error ? e.toString() : ""
        }, ${data ? JSON.stringify(data) : ""}`,
      });
    }
  }
};

const slugifyCmsKey = (key: string) =>
  key
    .split("/")
    .map(s => slugify(s, { replacement: "-", lower: true, trim: true }))
    .join("/");

const getCmsLocale = (locale: AppLocale) => {
  if (locale === "en-CH") {
    return "default";
  }
  return locale;
};

export const cmsService = (configParams: {
  cmsAccessToken: string;
  cmsVersion: CmsVersion;
  locale: AppLocale;
  useLocalCms?: boolean;
  cmsLinkWithEnv?: string;
}) => {
  const storyblok = new StoryblokClient({
    accessToken: configParams.cmsAccessToken,
  });

  const defaultParams = {
    version: configParams.cmsVersion,
    language: getCmsLocale(configParams.locale),
  };

  const get = async <T>(key: string, params?: ISbStoriesParams): Promise<CMSMultiStoryResult<T>> => {
    try {
      if (configParams.useLocalCms) {
        const response = await fetch(`${LOCAL_CMS_URL}/${slugifyCmsKey(key)}?language=${defaultParams.language}`);
        return response.json() as Promise<CMSMultiStoryResult<T>>;
      } else {
        return await storyblok.get(CMS_BASE_SLUG, {
          starts_with: slugifyCmsKey(key),
          ...defaultParams,
          ...params,
        });
      }
    } catch (_) {
      throw new Exceptions[500]({
        message: `Internal Server Error on CMS for ${key}`,
        response: defaultResponse,
        stack: `cmsService.get:${slugifyCmsKey(key)}}`,
      });
    }
  };

  const fetchFromSB = async <T>(key: string, params?: ISbStoriesParams): Promise<CMSStory<T>[]> =>
    (await storyblok.getAll(CMS_BASE_SLUG, {
      starts_with: slugifyCmsKey(key),
      ...defaultParams,
      ...params,
      per_page: 100,
    })) as unknown as Promise<CMSStory<T>[]>;

  const fetchFromCloudfront = async <T>(storiesPrefixes: string[]): Promise<CMSStory<T>[]> => {
    let allStories = {};
    for (const prefix of storiesPrefixes) {
      const response = await fetch(`${configParams.cmsLinkWithEnv}/cms-${prefix}-${configParams.locale}.json`, {
        cache: "no-store",
      });
      if (response?.ok) {
        const data = (await response.json()) as Record<string, CMSStory<T>>;
        allStories = { ...allStories, ...data };
      }
    }

    return Object.values(allStories);
  };

  const getAll = async <T>(
    key: string,
    storiesPrefixes: string[],
    params?: ISbStoriesParams,
  ): Promise<CMSStory<T>[]> => {
    try {
      if (configParams.useLocalCms) {
        const response = await fetch(`${LOCAL_CMS_URL}/get_all_story/?language=${defaultParams.language}`);
        return response.json() as Promise<CMSStory<T>[]>;
      } else {
        if (configParams.cmsLinkWithEnv) return fetchFromCloudfront(storiesPrefixes);
        return fetchFromSB(key, params);
      }
    } catch (_) {
      throw new Exceptions[500]({
        message: `Internal Server Error on CMS for ${key}`,
        response: defaultResponse,
        stack: `cmsService.getAll:${slugifyCmsKey(key)}}`,
      });
    }
  };

  return {
    ...storyblok,
    getAll,
    get,
  };
};

export const httpService = (configParams: {
  getAccessToken: (() => Promise<string | undefined>) | undefined;
  locale: AppLocale;
  origin?: string;
  signal?: AbortSignal;
}) => {
  getInternalAccessToken = configParams.getAccessToken;
  return {
    fetch: <T = undefined>(params: FetchApiParams) =>
      fetchApi<T>({
        ...params,
        getAccessToken: configParams.getAccessToken,
        origin: configParams.origin,
        signal: configParams.signal,
      }),
  };
};

export * from "./apiEndpoints";
export * from "./cmsEndpoints";
export * from "./uploadFileToS3";
export * from "./types";
